import type { LucideIcon } from "lucide-react";
import { GraduationCap, Search, Microscope, LineChart, Crown } from "lucide-react";

/**
 * The desk career — the single spine of the member portal.
 *
 * A member is hired as an Intern and earns promotion by doing the actual work
 * of the seat they sit in. Each promotion physically opens the next set of
 * tools. Belts (see tracks.ts) remain purely decorative per-module badges;
 * the SEAT is what unlocks.
 */

export type Seat = "intern" | "researcher" | "analyst" | "trader" | "senior_trader";

/** "researcher" is kept only as a legacy value — it merged into Intern and is no longer on the ladder. */
export const SEAT_ORDER: Seat[] = ["intern", "analyst", "trader", "senior_trader"];

/** Map any stored seat (including the retired Researcher seat) onto the live ladder. */
export function normalizeSeat(s: string | null | undefined): Seat {
  if (!s || s === "researcher") return "intern";
  return (SEAT_ORDER as string[]).includes(s) ? (s as Seat) : "intern";
}

/** A single measurable condition a member must satisfy to be promoted. */
export interface SeatRequirement {
  key: string;
  /** Shown verbatim on the promotion checklist. */
  label: string;
  /** How the member satisfies it, in one short line. */
  hint: string;
  /** Target count. `1` renders as a simple done / not-done tick. */
  target: number;
}

export interface SeatDef {
  key: Seat;
  /** Trading-desk title. */
  title: string;
  /** One-word verb describing the seat's job. */
  verb: string;
  blurb: string;
  icon: LucideIcon;
  accent: string;
  /** Tool keys this seat opens (cumulative — later seats inherit earlier ones). */
  unlocks: string[];
  /** What must be true to leave this seat for the next one. */
  promotion: SeatRequirement[];
  /** Prompt for the written defense that closes every Desk Review. */
  defensePrompt: string;
}

export const SEATS: Record<Seat, SeatDef> = {
  intern: {
    key: "intern",
    title: "Researcher",
    verb: "Learns",
    blurb:
      "You are on the floor to learn: the language, the topics, and how to read a chart. No capital, no positions — reps, Academy units and AI chart reviews.",
    icon: GraduationCap,
    accent: "#8A8F98",
    unlocks: ["training_hall", "intern_academy", "chart_reviews", "todays_call", "team_feed"],
    promotion: [
      {
        key: "wisdom",
        label: "Earn 150 wisdom points",
        hint: "Make simulation decisions on Kingdom Hub — every decision teaches something.",
        target: 150,
      },
      {
        key: "academy",
        label: "Finish all 5 Researcher levels (lessons + homework)",
        hint: "Basics → Charting → Indicators → Options → Higher concepts. Each level ends with homework you submit.",
        target: 5,
      },
      {
        key: "re_levels",
        label: "Finish Real Estate Academy levels 1–5 (lessons + homework)",
        hint: "Understand and See: why property builds wealth, investor math, listings, comps and neighborhoods.",
        target: 5,
      },
      {
        key: "charts_reviewed",
        label: "25 AI chart reviews with a follow-up check",
        hint: "Submit a marked-up chart, get the AI grade, then come back and log whether the call would have won.",
        target: 25,
      },
    ],
    defensePrompt:
      "Walk through one chart you reviewed. What was the setup, what did you expect, and did it win? What did that teach you?",
  },
  researcher: {
    key: "researcher",
    title: "Researcher",
    verb: "Studies",
    blurb:
      "Retired seat — merged into Intern.",
    icon: Search,
    accent: "#7A8B6F",
    unlocks: ["training_hall"],
    promotion: [],
    defensePrompt: "",
  },
  analyst: {
    key: "analyst",
    title: "Analyst",
    verb: "Researches",
    blurb:
      "You keep the trade journal: a top-down thesis, a marked-up chart, entry, stop and target. Paper trading without the buy or sell, scored against real prices.",
    icon: Microscope,
    accent: "#5B8FB9",
    unlocks: ["training_hall", "intern_academy", "chart_reviews", "todays_call", "team_feed", "factor_reads", "thesis_composer", "team_fund_pitch"],
    promotion: [
      {
        key: "wisdom",
        label: "Earn 800 wisdom points",
        hint: "Make simulation decisions on Kingdom Hub — every decision teaches something.",
        target: 800,
      },
      {
        key: "analyst_levels",
        label: "Finish all 4 Analyst levels (lessons + homework)",
        hint: "Research journal → Sector and company research → Testing ideas → Risk and sizing.",
        target: 4,
      },
      {
        key: "journal_volume",
        label: "50 trade theses (or 12 weeks of journaling)",
        hint: "Top-down thesis, chart with technical analysis, entry, stop and target. No buy or sell.",
        target: 50,
      },
      {
        key: "journal_win_rate",
        label: "55% of scored theses win",
        hint: "Each thesis is scored against real prices: target hit first = win, stop hit first = loss. AI chart review wins count too.",
        target: 55,
      },
    ],
    defensePrompt:
      "Name the one edge you keep returning to. Why does it exist, and who is on the other side of it?",
  },
  trader: {
    key: "trader",
    title: "Strategist",
    verb: "Positions",
    blurb:
      "The paper book is open. Every position needs a linked thesis and a stop. Your track record is now public.",
    icon: LineChart,
    accent: "#C5A059",
    unlocks: [
      "training_hall", "intern_academy", "chart_reviews", "todays_call", "team_feed", "team_fund_pitch",
      "thesis_composer",
            "factor_reads",
      "paper_book",
      "track_record",
    ],
    promotion: [
      {
        key: "wisdom",
        label: "Earn 1,500 wisdom points",
        hint: "Make simulation decisions on Kingdom Hub — every decision teaches something.",
        target: 1500,
      },
      {
        key: "closed_trades",
        label: "Close twelve paper trades",
        hint: "Entered, managed, and closed — winners and losers both count.",
        target: 12,
      },
      {
        key: "journaled",
        label: "Journal every closed trade",
        hint: "Lessons learned filled in — an unjournaled trade taught you nothing.",
        target: 12,
      },
      {
        key: "disciplined",
        label: "Follow your plan on at least half",
        hint: "Discipline is measured on 'followed plan', not on P&L.",
        target: 6,
      },
    ],
    defensePrompt:
      "Describe your worst loss on the book. What did the plan say, and where exactly did you leave it?",
  },
  senior_trader: {
    key: "senior_trader",
    title: "Senior Strategist",
    verb: "Plans",
    blurb:
      "You build the book, not just the trade. Allocate across positions against a risk budget — and grade the desk beneath you.",
    icon: Crown,
    accent: "#E0C088",
    unlocks: [
      "training_hall", "intern_academy", "chart_reviews", "todays_call", "team_feed", "team_fund_pitch",
      "thesis_composer",
            "factor_reads",
      "paper_book",
      "track_record",
      "portfolio_construction",
      "review_queue",
      "true_fast_weighted",
    ],
    promotion: [],
    defensePrompt: "",
  },
};

export const SEAT_LIST: SeatDef[] = SEAT_ORDER.map((s) => SEATS[s]);

export function seatIndex(seat: Seat): number {
  return Math.max(0, SEAT_ORDER.indexOf(seat));
}

/** True when `seat` is at or above `required`. */
export function seatAtLeast(seat: Seat, required: Seat): boolean {
  return seatIndex(seat) >= seatIndex(required);
}

export function nextSeat(seat: Seat): Seat | null {
  const i = seatIndex(seat);
  return i < SEAT_ORDER.length - 1 ? SEAT_ORDER[i + 1] : null;
}

/** Does this seat have the named tool open? */
export function seatUnlocks(seat: Seat, tool: string): boolean {
  return SEATS[seat].unlocks.includes(tool);
}

/** The lowest seat that opens the named tool — used for locked-state copy. */
export function seatRequiredFor(tool: string): Seat {
  return SEAT_ORDER.find((s) => SEATS[s].unlocks.includes(tool)) ?? "senior_trader";
}

/** Raw counters gathered from the member's actual work. */
export interface DeskCounters {
  modulesComplete: number;
  academyUnits: number;
  analystLevels: number;
  realEstateLevels: number;
  wisdom: number;
  thesesPublished: number;
  soundReviews: number;
  closedTrades: number;
  journaledTrades: number;
  disciplinedTrades: number;
  journalTheses: number;
  journalWeeks: number;
  journalScored: number;
  journalWins: number;
  chartsReviewed: number;
  chartsScored: number;
  chartWins: number;
}

export const EMPTY_COUNTERS: DeskCounters = {
  modulesComplete: 0,
  academyUnits: 0,
  analystLevels: 0,
  realEstateLevels: 0,
  wisdom: 0,
  thesesPublished: 0,
  soundReviews: 0,
  closedTrades: 0,
  journaledTrades: 0,
  disciplinedTrades: 0,
  journalTheses: 0,
  journalWeeks: 0,
  journalScored: 0,
  journalWins: 0,
  chartsReviewed: 0,
  chartsScored: 0,
  chartWins: 0,
};

const COUNTER_FOR: Record<string, keyof DeskCounters> = {
  modules: "modulesComplete",
  academy: "academyUnits",
  analyst_levels: "analystLevels",
  re_levels: "realEstateLevels",
  wisdom: "wisdom",
  theses: "thesesPublished",
  sound_reviews: "soundReviews",
  closed_trades: "closedTrades",
  journaled: "journaledTrades",
  disciplined: "disciplinedTrades",
  charts_reviewed: "chartsReviewed",
};

export interface RequirementStatus extends SeatRequirement {
  current: number;
  met: boolean;
  /** Overrides "current / target" on the checklist. */
  display?: string;
}

export interface PromotionStatus {
  seat: Seat;
  next: Seat | null;
  requirements: RequirementStatus[];
  /** All counted requirements satisfied — the written defense is still owed. */
  requirementsMet: boolean;
  /** 0..1 across the checklist. */
  progress: number;
}

export function evaluatePromotion(
  seat: Seat,
  counters: DeskCounters,
  foundation: RequirementStatus[] = [],
): PromotionStatus {
  const def = SEATS[seat];
  const desk: RequirementStatus[] = def.promotion.map((r) => {
    if (r.key === "journal_volume") {
      const met = counters.journalTheses >= 50 || counters.journalWeeks >= 12;
      const current = Math.max(counters.journalTheses, Math.round((counters.journalWeeks / 12) * 50));
      return { ...r, current: Math.min(current, 50), met, display: `${counters.journalTheses} theses · ${counters.journalWeeks} wks` };
    }
    if (r.key === "journal_win_rate") {
      const scored = counters.journalScored + (counters.chartsScored ?? 0);
      const wins = counters.journalWins + (counters.chartWins ?? 0);
      const rate = scored ? Math.round((wins / scored) * 100) : 0;
      const met = scored >= 20 && rate >= 55;
      return { ...r, current: Math.min(rate, 55), met, display: scored ? `${rate}% of ${scored}${scored < 20 ? " (need 20 scored)" : ""}` : "none scored yet" };
    }
    const current = counters[COUNTER_FOR[r.key]] ?? 0;
    return { ...r, current, met: current >= r.target };
  });
  const requirements = def.promotion.length ? [...foundation, ...desk] : [];
  const requirementsMet = requirements.length > 0 && requirements.every((r) => r.met);
  const progress = requirements.length
    ? requirements.reduce((s, r) => s + (r.met ? 1 : Math.min(1, r.current / r.target)), 0) / requirements.length
    : 1;
  return { seat, next: nextSeat(seat), requirements, requirementsMet, progress };
}

/**
 * Seat-weighted voice in True Fast funding. Standing earned through training
 * translates into weight — an Intern still gets a voice, just a quieter one.
 */
export const SEAT_PLEDGE_WEIGHT: Record<Seat, number> = {
  intern: 1,
  researcher: 1.25,
  analyst: 1.5,
  trader: 2,
  senior_trader: 3,
};

/**
 * The other two tracks run the same four seats, re-skinned. Content is
 * authored in a later pass; these titles keep the ladder visible today.
 */
export const TRACK_SEAT_TITLES: Record<string, Record<Seat, string>> = {
  trade: {
    intern: "Researcher",
    researcher: "Researcher",
    analyst: "Analyst",
    trader: "Strategist",
    senior_trader: "Senior Strategist",
  },
  real_estate: {
    intern: "Apprentice",
    researcher: "Researcher",
    analyst: "Underwriter",
    trader: "Operator",
    senior_trader: "Principal",
  },
  business: {
    intern: "Apprentice",
    researcher: "Researcher",
    analyst: "Analyst",
    trader: "Buyer",
    senior_trader: "Owner-Operator",
  },
};

export function seatTitle(seat: Seat, trackId = "trade"): string {
  return TRACK_SEAT_TITLES[trackId]?.[seat] ?? SEATS[seat].title;
}
/** The Desk motto, shown at every rank. */
export const DESK_MOTTO = "Obedience · Honor · Discipline";

/** Money Foundation → Learn → Apply grouping for the promotion checklist. */
export function requirementPhase(key: string): "foundation" | "learn" | "apply" {
  if (key.startsWith("f_")) return "foundation";
  return key === "modules" || key === "academy" || key === "analyst_levels" || key === "re_levels" || key === "charts_reviewed" ? "learn" : "apply";
}

/** Plain-language names for every seat tool, used by the "What this seat opens" panel. */
export const TOOL_LABELS: Record<string, string> = {
  training_hall: "Training Hall",
  intern_academy: "Researcher Academy",
  chart_reviews: "AI chart reviews",
  todays_call: "Today's Call",
  team_feed: "Team feed: read and react",
  thesis_composer: "Trade Journal",
  team_fund_pitch: "Pitch to the Team Fund",
  factor_reads: "Sector reads",
  paper_book: "My Book: options and paper trades",
  track_record: "Public track record",
  portfolio_construction: "Portfolio planning",
  review_queue: "Review tab: grade theses and promotion applications",
  true_fast_weighted: "Extra weight in True Fast votes",
};

/** Seats that go to a human reviewer instead of being checked automatically. */
export function needsReview(to: Seat): boolean {
  return to === "trader" || to === "senior_trader";
}
